import { Select, Text } from '@viapeople/components'
import { useAggregationStore } from '../../stores/aggregationStore'
import { DISTRIBUTION_TIERS, DistributionBar, type DistributionCounts, getRatingColor } from '../shared'
import { useRatingScaleMax } from '../useRatingScaleMax'

type QuestionDistributionEntry = { label: string; min: number; max: number; count: number }

function toDistributionCounts(buckets: QuestionDistributionEntry[]): DistributionCounts {
	const counts: DistributionCounts = {
		critical: 0,
		needsImprovement: 0,
		average: 0,
		good: 0,
		excellent: 0,
		total: 0,
	}
	for (const b of buckets) {
		switch (b.label) {
			case 'Critical':
				counts.critical = b.count
				break
			case 'Needs Improvement':
				counts.needsImprovement = b.count
				break
			case 'Average':
				counts.average = b.count
				break
			case 'Good':
				counts.good = b.count
				break
			case 'Excellent':
				counts.excellent = b.count
				break
		}
		counts.total += b.count
	}
	return counts
}

function RatingChip({ value }: { value: number | null }) {
	const scaleMax = useRatingScaleMax()
	if (value === null) return <span className='text-xs text-neutral-400'>—</span>
	return (
		<span
			className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-semibold ${getRatingColor(value, scaleMax)}`}>
			{value.toFixed(2)}
		</span>
	)
}

function VsOverall({ value }: { value: number | null }) {
	if (value === null) return <span className='text-xs text-neutral-400'>—</span>
	const sign = value > 0 ? '+' : ''
	const className = value > 0 ? 'text-emerald-600' : value < 0 ? 'text-red-600' : 'text-neutral-600'
	return (
		<span className={`text-sm font-semibold ${className}`}>
			{sign}
			{value.toFixed(2)}
		</span>
	)
}

export function PerformanceByQuestion() {
	const {
		cycleTemplates,
		participantSelectedTemplateId,
		selectParticipantTemplate,
		participantPerformanceByQuestion,
		isParticipantPerformanceLoading,
	} = useAggregationStore()

	const hasTemplates = cycleTemplates.length > 0

	return (
		<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-5 text-left'>
			<div className='flex items-center justify-between gap-4'>
				<Text variant='large' weight='semibold'>
					Performance by Question
				</Text>
				{hasTemplates && (
					<div className='flex items-center gap-2'>
						<span className='text-sm text-neutral-500'>Cycle Template</span>
						<div className='w-64'>
							<Select
								sizing='small'
								options={cycleTemplates.map((t) => ({
									value: t.templateId,
									label: `${t.templateName} (${t.cycleCount} ${t.cycleCount === 1 ? 'Deal' : 'Deals'})`,
								}))}
								value={participantSelectedTemplateId ?? ''}
								onChange={(v: string) => selectParticipantTemplate(v)}
							/>
						</div>
					</div>
				)}
			</div>

			{!hasTemplates && (
				<div className='flex items-center justify-center py-10 text-neutral-400'>
					<Text variant='small'>No deal templates found for this organization.</Text>
				</div>
			)}

			{hasTemplates && (
				<>
					<div className='flex flex-wrap gap-x-6 gap-y-2'>
						{DISTRIBUTION_TIERS.map((tier) => (
							<div key={tier.key} className='flex items-center gap-1.5'>
								<div className='h-3 w-3 rounded shrink-0' style={{ backgroundColor: tier.color }} />
								<span className='text-xs text-neutral-600'>{tier.label}</span>
							</div>
						))}
					</div>

					{!isParticipantPerformanceLoading &&
						participantPerformanceByQuestion &&
						participantPerformanceByQuestion.length === 0 && (
							<div className='flex items-center justify-center py-10 text-neutral-400'>
								<Text variant='small'>No rating questions found for this template.</Text>
							</div>
						)}

					{participantPerformanceByQuestion && participantPerformanceByQuestion.length > 0 && (
						<div
							className={`rounded-lg border border-neutral-200 flex flex-col divide-y divide-neutral-100 transition-opacity duration-150 ${
								isParticipantPerformanceLoading ? 'opacity-40 pointer-events-none' : 'opacity-100'
							}`}>
							{participantPerformanceByQuestion.map((q, idx) => (
								<div key={`${q.questionLabel}-${idx}`} className='px-6 py-5 flex flex-col gap-3'>
									<p className='text-base font-normal text-neutral-900'>{q.questionLabel}</p>
									<div className='flex items-center gap-6'>
										<div className='flex flex-col gap-1 flex-1 min-w-0'>
											<span className='text-xs text-neutral-400'>Rating Distribution</span>
											<DistributionBar distribution={toDistributionCounts(q.distribution)} />
										</div>
										<div className='flex flex-col items-center gap-1 shrink-0'>
											<span className='text-xs text-neutral-500'>Participant Rating</span>
											<RatingChip value={q.participantRating} />
										</div>
										<div className='flex flex-col items-center gap-1 shrink-0'>
											<span className='text-xs text-neutral-500'>Overall Rating</span>
											<RatingChip value={q.overallRating} />
										</div>
										<div className='flex flex-col items-center gap-1 shrink-0 min-w-20'>
											<span className='text-xs text-neutral-500'>Rating vs. Overall</span>
											<VsOverall value={q.vsOverall} />
										</div>
									</div>
								</div>
							))}
						</div>
					)}

					{isParticipantPerformanceLoading && !participantPerformanceByQuestion && (
						<div className='flex items-center justify-center py-10 text-neutral-400'>
							<Text variant='small'>Loading…</Text>
						</div>
					)}
				</>
			)}
		</div>
	)
}
